import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ExploreIcon from '@mui/icons-material/Explore';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PortraitIcon from '@mui/icons-material/Portrait';
import ScienceIcon from '@mui/icons-material/Science';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';

import { Link, useLocation } from 'react-router-dom';
import Logo from '../../../../components/logo/logo.component';
import {
  NavigationContainer,
  NavSection,
  NavSectionLabel,
  ActiveIndicator,
  NavItemWrapper,
  UserSectionContainer,
} from './navigation.styles';
import { useAuth } from '../../../../context/auth.context';
import { usePosts } from '../../../../context/posts.context';
import { useMockData } from '../../../../context/mock-data.context';
import { ThemeProviderContext } from '../../../../context/theme.context';
import { getInitials } from '../../../../utils/get-initials.util';
import { notificationsService, messagesService } from '../../../../services/service-proxy';
import { CreateMenu } from '../../../../components/create-menu/create-menu.component';

const isDev = process.env.NODE_ENV === 'development';

const Navigation = () => {
  const { logout, profile, user } = useAuth();
  const { openNewPost, openWorkoutPost, openMealPost, openProgressPost } = usePosts();
  const { isMockEnabled, toggleMock } = useMockData();
  const { themeMode, toggleTheme } = useContext(ThemeProviderContext);
  const location = useLocation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  const [userDropdownAnchorEl, setUserDropdownAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState(null);
  const createButtonRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    const unsubscribe = notificationsService.onUnreadCountChange(user.uid, (count) => {
      setUnreadCount(count);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadMessages(0);
      return;
    }
    const unsubscribe = messagesService.onTotalUnreadChange(user.uid, (count) => {
      setUnreadMessages(count);
    });
    return () => unsubscribe();
  }, [user]);

  const handleUserDropdownOpen = (event) => {
    setUserDropdownAnchorEl(event.currentTarget);
  };

  const handleUserDropdownClose = () => {
    setUserDropdownAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserDropdownClose();
    try {
      await logout();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout failed:', err);
      }
    }
  };

  const handleCreateMenuOpen = (event) => {
    setCreateMenuAnchorEl(event.currentTarget);
  };

  const handleCreateMenuClose = () => {
    setCreateMenuAnchorEl(null);
  };

  const handleQuickPost = () => {
    handleCreateMenuClose();
    openNewPost();
  };

  const handleLogWorkout = () => {
    handleCreateMenuClose();
    openWorkoutPost();
  };

  const handleLogMeal = () => {
    handleCreateMenuClose();
    openMealPost();
  };

  const handleProgressPhoto = () => {
    handleCreateMenuClose();
    openProgressPost();
  };

  const displayName = profile?.displayName || 'User';
  const username = profile?.username || '';
  const avatarUrl = profile?.avatarUrl || '';

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const profilePath = username ? `/profile/${username}` : '/profile';

  // Get the theme icon and tooltip based on current mode
  const getThemeIcon = () => {
    if (themeMode === 'light') return <LightModeIcon fontSize="small" />;
    if (themeMode === 'dark') return <DarkModeIcon fontSize="small" />;
    return <SettingsBrightnessIcon fontSize="small" />;
  };

  const getThemeTooltip = () => {
    if (themeMode === 'light') return 'Theme: Light (click to switch)';
    if (themeMode === 'dark') return 'Theme: Dark (click to switch)';
    return 'Theme: System (click to switch)';
  };

  const primaryNavItems = [
    {
      label: 'Home',
      activeIcon: <HomeIcon />,
      inactiveIcon: <HomeOutlinedIcon />,
      to: '/home',
      active: isActive('/home'),
    },
    {
      label: 'Explore',
      activeIcon: <ExploreIcon />,
      inactiveIcon: <ExploreOutlinedIcon />,
      to: '/explore',
      active: isActive('/explore'),
    },
    {
      label: 'Search',
      activeIcon: <SearchIcon />,
      inactiveIcon: <SearchOutlinedIcon />,
      to: '/search',
      active: isActive('/search'),
    },
    {
      label: 'Workouts',
      activeIcon: <FitnessCenterIcon />,
      inactiveIcon: <FitnessCenterOutlinedIcon />,
      to: '/workouts',
      active: isActive('/workouts'),
    },
    {
      label: 'Nutrition',
      activeIcon: <RestaurantIcon />,
      inactiveIcon: <RestaurantOutlinedIcon />,
      to: '/nutrition',
      active: isActive('/nutrition'),
    },
    {
      label: 'Accountability',
      activeIcon: <HandshakeIcon />,
      inactiveIcon: <HandshakeOutlinedIcon />,
      to: '/progress',
      active: isActive('/progress'),
    },
  ];

  const socialNavItems = [
    {
      label: 'Notifications',
      activeIcon: (
        <Badge badgeContent={unreadCount} color="secondary" max={99}>
          <NotificationsIcon />
        </Badge>
      ),
      inactiveIcon: (
        <Badge badgeContent={unreadCount} color="secondary" max={99}>
          <NotificationsOutlinedIcon />
        </Badge>
      ),
      to: '/notifications',
      active: isActive('/notifications'),
    },
    {
      label: 'Messages',
      activeIcon: (
        <Badge badgeContent={unreadMessages} color="secondary" max={99}>
          <ChatBubbleIcon />
        </Badge>
      ),
      inactiveIcon: (
        <Badge badgeContent={unreadMessages} color="secondary" max={99}>
          <ChatBubbleOutlineIcon />
        </Badge>
      ),
      to: '/messages',
      active: isActive('/messages'),
    },
    {
      label: 'Saved',
      activeIcon: <BookmarkIcon />,
      inactiveIcon: <BookmarkBorderIcon />,
      to: '/saved',
      active: isActive('/saved'),
    },
    {
      label: 'Profile',
      activeIcon: <PersonIcon />,
      inactiveIcon: <PersonOutlineIcon />,
      to: profilePath,
      active: isActive('/profile'),
    },
  ];

  const renderNavItem = (item) => (
    <NavItemWrapper key={item.label}>
      {item.active && <ActiveIndicator />}
      <Tooltip
        title={isTablet ? item.label : ''}
        placement="right"
        arrow
      >
        <ListItem
          button
          component={Link}
          to={item.to}
          selected={item.active}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            justifyContent: isTablet ? 'center' : 'flex-start',
            px: isTablet ? 1 : 2,
            py: 1,
            transition: `background-color ${theme.transitions.duration.short}ms`,
            '&.Mui-selected': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
          }}
          aria-label={item.label}
          aria-current={item.active ? 'page' : undefined}
        >
          <ListItemIcon
            sx={{
              minWidth: isTablet ? 0 : 40,
              justifyContent: 'center',
              color: item.active ? 'primary.main' : 'text.secondary',
              transition: `color ${theme.transitions.duration.short}ms`,
            }}
          >
            {item.active ? item.activeIcon : item.inactiveIcon}
          </ListItemIcon>
          {!isTablet && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: item.active ? 700 : 400,
                color: item.active ? 'text.primary' : 'text.secondary',
              }}
            />
          )}
        </ListItem>
      </Tooltip>
    </NavItemWrapper>
  );

  return (
    <NavigationContainer component="nav" aria-label="Main navigation">
      <Box sx={{ width: '100%' }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: isTablet ? 'center' : 'flex-start', mb: 2 }}>
          <Logo noText={isTablet} />
        </Box>

        {/* Primary nav */}
        <NavSection>
          {!isTablet && <NavSectionLabel>Navigate</NavSectionLabel>}
          <List disablePadding>
            {primaryNavItems.map(renderNavItem)}
          </List>
        </NavSection>

        {/* Social nav */}
        <NavSection>
          {!isTablet && <NavSectionLabel>Social</NavSectionLabel>}
          <List disablePadding>
            {socialNavItems.map(renderNavItem)}
          </List>
        </NavSection>

        {/* Create button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 2,
          }}
        >
          {isTablet ? (
            <Tooltip title="Create" placement="right" arrow>
              <IconButton
                ref={createButtonRef}
                color="primary"
                onClick={handleCreateMenuOpen}
                aria-label="Create new content"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  width: 44,
                  height: 44,
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Box
              ref={createButtonRef}
              component="button"
              onClick={handleCreateMenuOpen}
              aria-label="Create new content"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                borderRadius: '40px',
                px: 4,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 700,
                width: '100%',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                fontFamily: 'inherit',
                transition: `background-color ${theme.transitions.duration.short}ms`,
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
              Create
            </Box>
          )}
        </Box>

        {/* Create menu dropdown */}
        <CreateMenu
          anchorEl={createMenuAnchorEl}
          open={Boolean(createMenuAnchorEl)}
          onClose={handleCreateMenuClose}
          onQuickPost={handleQuickPost}
          onLogWorkout={handleLogWorkout}
          onLogMeal={handleLogMeal}
          onProgressPhoto={handleProgressPhoto}
          variant="dropdown"
        />
      </Box>

      {/* Theme toggle + Mock data toggle */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pb: 1, width: '100%' }}>
        {/* Theme toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Tooltip title={getThemeTooltip()} placement="right" arrow>
            <IconButton
              onClick={toggleTheme}
              aria-label="Toggle theme"
              size="small"
              sx={{
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'primary.main',
                },
              }}
            >
              {getThemeIcon()}
            </IconButton>
          </Tooltip>
          {!isTablet && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                ml: 1,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                textTransform: 'capitalize',
              }}
              onClick={toggleTheme}
            >
              {themeMode}
            </Typography>
          )}
        </Box>

        {/* Mock data toggle - development only */}
        {isDev && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title={isMockEnabled ? 'Mock Data: ON (click to disable)' : 'Toggle Mock Data'} placement="right" arrow>
              <IconButton
                onClick={toggleMock}
                aria-label="Toggle mock data"
                size="small"
                sx={{
                  color: isMockEnabled ? 'secondary.main' : 'text.disabled',
                  backgroundColor: isMockEnabled ? 'action.selected' : 'transparent',
                  border: isMockEnabled ? '1px solid' : '1px dashed',
                  borderColor: isMockEnabled ? 'secondary.main' : 'divider',
                  '&:hover': {
                    backgroundColor: isMockEnabled ? 'action.hover' : 'action.hover',
                  },
                }}
              >
                {isMockEnabled ? (
                  <Badge variant="dot" color="secondary">
                    <ScienceIcon fontSize="small" />
                  </Badge>
                ) : (
                  <ScienceOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {!isTablet && (
              <Typography
                variant="caption"
                color={isMockEnabled ? 'secondary.main' : 'text.disabled'}
                sx={{ ml: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                onClick={toggleMock}
              >
                {isMockEnabled ? 'Mock ON' : 'Mock Data'}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* User section at bottom */}
      <UserSectionContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: isTablet ? 'center' : 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {isTablet ? (
            <Tooltip title={displayName} placement="right" arrow>
              <IconButton
                onClick={handleUserDropdownOpen}
                aria-label="User menu"
                sx={{ p: 0 }}
              >
                <Avatar
                  src={avatarUrl}
                  alt={`${displayName}'s avatar`}
                  sx={{ width: 36, height: 36 }}
                >
                  {!avatarUrl && getInitials(displayName)}
                </Avatar>
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minWidth: 0 }}
                component={Link}
                to={profilePath}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Avatar
                  src={avatarUrl}
                  alt={`${displayName}'s avatar`}
                  sx={{ width: 36, height: 36 }}
                >
                  {!avatarUrl && getInitials(displayName)}
                </Avatar>
                <Box sx={{ ml: 1.5, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {displayName}
                  </Typography>
                  {username && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      @{username}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  component={Link}
                  to="/settings"
                  aria-label="Settings"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={handleUserDropdownOpen}
                  aria-label="User menu"
                  size="small"
                >
                  <ExitToAppIcon fontSize="small" />
                </IconButton>
              </Box>
            </>
          )}
        </Box>
        <Menu
          anchorEl={userDropdownAnchorEl}
          open={Boolean(userDropdownAnchorEl)}
          onClose={handleUserDropdownClose}
          PaperProps={{
            elevation: 3,
            sx: {
              overflow: 'visible',
              mb: 1.5,
              p: 1,
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          <MenuItem
            component={Link}
            to={profilePath}
            onClick={handleUserDropdownClose}
          >
            <ListItemIcon><PortraitIcon /></ListItemIcon>
            <ListItemText primary="View Profile" />
          </MenuItem>
          <MenuItem
            component={Link}
            to="/settings"
            onClick={handleUserDropdownClose}
          >
            <ListItemIcon><SettingsIcon /></ListItemIcon>
            <ListItemText primary="Account Settings" />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><ExitToAppIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>
      </UserSectionContainer>
    </NavigationContainer>
  );
};

export default Navigation;
